import {ArrowUpDown, Check, LayoutGrid, List, Search} from "lucide-react";
import type {ReactNode} from "react";
import {clsxMerge} from "../../utilis/classNameUtils.ts";
import {TEAM_GROUPS, TeamFilter} from "./teamFilters";
import {StatusDot} from "./TeamMemberParts";
import {TeamView, TeamViewPrefs} from "./teamViewPrefs";

interface TeamToolbarProps {
    filter: TeamFilter;
    onFilterChange: (filter: TeamFilter) => void;
    query: string;
    onQueryChange: (query: string) => void;
    prefs: TeamViewPrefs;
    onPrefsChange: (prefs: TeamViewPrefs) => void;
}

// Design System toolbar: 32px chips (brand tint when chosen, never dark pills),
// a bordered 240px search, muted 36px buttons and a segmented Grid | List switch.
const chip = 'flex h-8 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-chip px-3 text-footnote font-medium';
const chipTone = (active: boolean) => active ? 'bg-brand-tint text-brand-ink font-semibold' : 'bg-surface-muted text-ink hover:bg-line';
const toolButton = 'flex h-control-md cursor-pointer items-center gap-1.5 rounded-control px-3 text-callout font-medium';

const TeamToolbar = ({filter, onFilterChange, query, onQueryChange, prefs, onPrefsChange}: TeamToolbarProps) => {
    const viewButton = (view: TeamView, label: string, icon: ReactNode) => (
        <button
            type="button"
            aria-pressed={prefs.view === view}
            onClick={() => onPrefsChange({...prefs, view})}
            className={clsxMerge(
                'flex h-8 cursor-pointer items-center gap-1.5 rounded-chip px-2.5 text-footnote font-medium',
                prefs.view === view ? 'bg-segment-selected text-ink shadow-segment' : 'text-ink-muted-strong hover:text-ink',
            )}
        >
            {icon}{label}
        </button>
    );

    return (
        <div className="flex min-h-14 flex-wrap items-center gap-2">
            <div role="group" aria-label="Filter by status" className="flex flex-wrap items-center gap-2">
                <button type="button" aria-pressed={filter === 'ALL'} onClick={() => onFilterChange('ALL')}
                        className={clsxMerge(chip, chipTone(filter === 'ALL'))}>
                    All
                </button>
                {TEAM_GROUPS.map(group => (
                    <button key={group.key} type="button" aria-pressed={filter === group.key}
                            onClick={() => onFilterChange(group.key)}
                            className={clsxMerge(chip, chipTone(filter === group.key))}>
                        <StatusDot group={group.key}/>
                        {group.label}
                    </button>
                ))}
            </div>

            <div className="ml-auto flex flex-wrap items-center gap-2">
                <label className="flex h-control-md w-[240px] items-center gap-2 rounded-control border border-solid border-fill-subtle bg-surface px-3 text-ink-muted focus-within:ring-2 focus-within:ring-brand-focus">
                    <Search size={16} className="!p-0 shrink-0 !text-current"/>
                    <input
                        type="search"
                        value={query}
                        placeholder="Search team…"
                        aria-label="Search team"
                        onChange={(event) => onQueryChange(event.target.value)}
                        className="min-w-0 flex-1 !border-0 !bg-transparent !p-0 text-callout text-ink !shadow-none !outline-none !ring-0 focus:!ring-0 [&::-webkit-search-cancel-button]:appearance-none"
                    />
                </label>

                <button type="button" className={clsxMerge(toolButton, 'bg-surface-muted text-ink hover:bg-line')}
                        onClick={() => onPrefsChange({...prefs, sort: prefs.sort === 'name' ? 'status' : 'name'})}>
                    <ArrowUpDown size={16} className="!p-0 !text-current"/>
                    Sort: {prefs.sort === 'name' ? 'Name' : 'Status'}
                </button>

                <button type="button" aria-pressed={prefs.grouped}
                        className={clsxMerge(toolButton, prefs.grouped ? 'bg-brand-tint text-brand-ink' : 'bg-surface-muted text-ink hover:bg-line')}
                        onClick={() => onPrefsChange({...prefs, grouped: !prefs.grouped})}>
                    {prefs.grouped && <Check size={16} className="!p-0 !text-current"/>}
                    Group
                </button>

                <div role="group" aria-label="View" className="flex h-control-md items-center gap-0.5 rounded-control bg-surface-muted p-0.5">
                    {viewButton('grid', 'Grid', <LayoutGrid size={14} className="!p-0 !text-current"/>)}
                    {viewButton('list', 'List', <List size={14} className="!p-0 !text-current"/>)}
                </div>
            </div>
        </div>
    );
};

export default TeamToolbar;
